/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useMemo
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Asset, CRUDPermissions, DownloadJobType} from '@ion/core/src/types/entities';
import {HeaderButton} from '@ion/dls/src/grid/HeaderButton';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

// defines the Task Device remove button prop definitions ...
export interface RemoveDeviceButtonTypes {
  selectedDevice: Asset[];
  downloadjobs: any;
  onRemoveDevice: (device: Asset) => void;
  jobsPermissions?: CRUDPermissions;
}

/**
 * Defines the RemoveDeviceButton component. This component contains
 * the row actions for Task Device grid.
 */
export const RemoveDeviceButton = ({
  downloadjobs,
  onRemoveDevice,
  jobsPermissions,
  selectedDevice = []
}: RemoveDeviceButtonTypes) => {
  const iconColor = dlsColor('white');

  const onActionWithSelectedRows = useCallback((callback): MouseEventHandler =>
    () => callback?.(selectedDevice[0]),
  [selectedDevice]);

  // Checking Task status and only allow the In-Progress task to remove device...
  const isJobInProgress = useMemo(() => {
    const progressStatus = downloadjobs?.progressStatus;
    return (
      progressStatus === DownloadJobType.Inprogress
    );
  }, [downloadjobs]);

  /* Checking if the job status is not in-progress and the selected device length.
  If the length is more than 1 we diabled the Remove Device button.. */
  const isDisabled = useMemo(() => {
    const length              = selectedDevice?.length || 0;
    const isSingleRowSelected = length !== 1;
    return (!isJobInProgress || isSingleRowSelected);
  }, [selectedDevice]);

  return (
    <>
      <HeaderButton disabled={isDisabled || !jobsPermissions?.hasDeletePermission}
        onClick={onActionWithSelectedRows(onRemoveDevice)}
        icon={<SystemDeleteIcon color={iconColor} />}
        label={translate('common:editors.delete.deviceRemove')} />
    </>
  );
};

export default RemoveDeviceButton;
